import React, { useMemo } from 'react';
import { Briefcase } from 'lucide-react';
import { Avatar, ROLE_COLORS, NA, ReachOutButtons, ViewScheduleButton } from './StaffDirectoryTab';

// Builds a reports-to tree: staff with a null/unresolvable reportsToStaffId are roots,
// open positions (status 'open') attach as extra child nodes under their reportsToStaffId.
// A `visited` guard in the renderer protects against a reports-to cycle the backend
// doesn't fully rule out (only self-reporting is blocked, not longer chains).
function buildOrgTree(staff, openPositions) {
  const staffById = new Map(staff.map(s => [s.id, s]));
  const childrenOf = new Map();
  const roots = [];

  staff.forEach(s => {
    if (s.reportsToStaffId != null && staffById.has(s.reportsToStaffId)) {
      if (!childrenOf.has(s.reportsToStaffId)) childrenOf.set(s.reportsToStaffId, []);
      childrenOf.get(s.reportsToStaffId).push(s);
    } else {
      roots.push(s);
    }
  });

  const openByReportsTo = new Map();
  const rootOpenPositions = [];
  openPositions.filter(p => p.status === 'open').forEach(p => {
    if (p.reportsToStaffId != null && staffById.has(p.reportsToStaffId)) {
      if (!openByReportsTo.has(p.reportsToStaffId)) openByReportsTo.set(p.reportsToStaffId, []);
      openByReportsTo.get(p.reportsToStaffId).push(p);
    } else {
      rootOpenPositions.push(p);
    }
  });

  return { roots, childrenOf, openByReportsTo, rootOpenPositions };
}

function StaffNode({ member, theme, onMessage }) {
  const roleColor = ROLE_COLORS[member.role] || 'bg-gray-100 text-gray-700';
  return (
    <div className={`${theme.card} border ${theme.border} rounded-xl p-4 flex flex-col gap-3 w-72`}>
      <div className="flex items-start gap-3">
        <Avatar member={member} />
        <div className="flex-1 min-w-0">
          <p className={`font-semibold ${theme.text} truncate`}>{member.name}</p>
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${roleColor}`}>
            {member.role ? member.role.charAt(0).toUpperCase() + member.role.slice(1) : NA}
          </span>
        </div>
      </div>
      <div className="space-y-1">
        <p className={`text-xs ${theme.textSecondary} truncate`}>{member.email || NA}</p>
        <p className={`text-xs ${theme.textSecondary}`}>{member.phoneNumber || NA}</p>
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-800">
        <ReachOutButtons member={member} onMessage={onMessage} />
        <ViewScheduleButton compact />
      </div>
    </div>
  );
}

function OpenPositionNode({ position, theme }) {
  return (
    <div className={`border-2 border-dashed ${theme.border} rounded-xl p-4 flex flex-col gap-3 w-72 bg-transparent`}>
      <div className="flex items-center gap-2">
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
          Open Position
        </span>
      </div>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full border-2 border-dashed ${theme.border} flex items-center justify-center flex-shrink-0 ${theme.textMuted}`}>
          <Briefcase size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-semibold ${theme.text} truncate`}>{position.title}</p>
          {position.role && <p className={`text-xs ${theme.textMuted}`}>{position.role}</p>}
        </div>
      </div>
      <p className={`text-xs ${theme.textSecondary}`}>
        Reports to: {position.reportsTo?.name || NA}
      </p>
    </div>
  );
}

function OrgBranch({ nodeId, member, tree, theme, visited, onMessage }) {
  if (visited.has(nodeId)) return null; // cycle guard
  const nextVisited = new Set(visited);
  nextVisited.add(nodeId);

  const childStaff = tree.childrenOf.get(nodeId) || [];
  const childPositions = tree.openByReportsTo.get(nodeId) || [];
  const hasChildren = childStaff.length > 0 || childPositions.length > 0;

  return (
    <div className="flex flex-col items-start">
      <StaffNode member={member} theme={theme} onMessage={onMessage} />
      {hasChildren && (
        <div className="ml-6 mt-4 pl-6 border-l-2 border-gray-200 dark:border-gray-700 flex flex-col gap-4">
          {childStaff.map(child => (
            <OrgBranch
              key={`staff-${child.id}`}
              nodeId={child.id}
              member={child}
              tree={tree}
              theme={theme}
              visited={nextVisited}
            onMessage={onMessage} />
          ))}
          {childPositions.map(position => (
            <OpenPositionNode key={`position-${position.id}`} position={position} theme={theme} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function OrgChartView({ staff, openPositions, theme, onMessage }) {
  const tree = useMemo(() => buildOrgTree(staff, openPositions), [staff, openPositions]);

  return (
    <div className="overflow-x-auto pb-4">
      <div className="flex flex-col gap-6 min-w-max">
        {tree.roots.map(root => (
          <OrgBranch
            key={`staff-${root.id}`}
            nodeId={root.id}
            member={root}
            tree={tree}
            theme={theme}
            visited={new Set()}
          onMessage={onMessage} />
        ))}
        {tree.rootOpenPositions.length > 0 && (
          <div className="flex flex-col items-start">
            {tree.roots.length > 0 && (
              <p className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-2`}>
                Unassigned open positions
              </p>
            )}
            <div className="flex flex-wrap gap-4">
              {tree.rootOpenPositions.map(position => (
                <OpenPositionNode key={`position-${position.id}`} position={position} theme={theme} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
